import type { FC, ReactNode } from 'react'
import type { InterestOptions, PageSection, SiteSettings } from '@/types'

import { Mail, MapPin, Phone } from 'lucide-react'
import { cms } from '@/lib/cms'

import Reveal from '@/components/landing/reveal'
import ContactForm from '@/components/landing/contact-form'
import SectionIntro from '@/components/landing/section-intro'

interface ContactSectionProps {
    section: PageSection | null
    settings: SiteSettings | null
    interests: InterestOptions
    defaultInterest?: string
    id?: string
}

const ContactSection: FC<ContactSectionProps> = ({
    section,
    settings,
    interests,
    defaultInterest,
    id = 'contact'
}): ReactNode => {
    const headingId = `${id}-heading`

    return (
        <section id={id} aria-labelledby={headingId} className='bg-stone py-24 sm:py-32'>
            <div className='mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-20'>
                <Reveal className='lg:col-span-5'>
                    {section
                        ? <SectionIntro section={section} headingId={headingId} />
                        : <h2 id={headingId} className='display text-3xl sm:text-4xl'>Start a conversation</h2>}

                    {settings && (settings.email || settings.phone || settings.address) && (
                        <ul className='mt-10 space-y-5 border-t border-foreground/15 pt-8'>
                            {settings.email && (
                                <li className='flex items-start gap-4'>
                                    <Mail className='mt-1 size-5 shrink-0 text-clay' aria-hidden='true' />
                                    <a href={`mailto:${settings.email}`} className='break-all text-lg hover:text-ocean focus-visible:underline focus-visible:outline-none'>
                                        <span data-mdlfy-cms={cms('site_settings', settings.id, 'email')}>{settings.email}</span>
                                    </a>
                                </li>
                            )}

                            {settings.phone && (
                                <li className='flex items-start gap-4'>
                                    <Phone className='mt-1 size-5 shrink-0 text-clay' aria-hidden='true' />
                                    <a href={`tel:${settings.phone.replace(/[^\d+]/g, '')}`} className='text-lg hover:text-ocean focus-visible:underline focus-visible:outline-none'>
                                        <span data-mdlfy-cms={cms('site_settings', settings.id, 'phone')}>{settings.phone}</span>
                                    </a>
                                </li>
                            )}

                            {settings.address && (
                                <li className='flex items-start gap-4'>
                                    <MapPin className='mt-1 size-5 shrink-0 text-clay' aria-hidden='true' />
                                    <span data-mdlfy-cms={cms('site_settings', settings.id, 'address')} className='whitespace-pre-line text-lg'>{settings.address}</span>
                                </li>
                            )}
                        </ul>
                    )}

                    {section?.image && (
                        <div className='mt-12 hidden aspect-[4/3] overflow-hidden bg-sand lg:block'>
                            <img src={section.image} alt={section.image_alt ?? ''} loading='lazy' className='size-full object-cover' />
                        </div>
                    )}
                </Reveal>

                <Reveal delay={120} className='lg:col-span-7'>
                    <div className='relative bg-background p-6 shadow-[0_30px_80px_-40px_rgba(22,22,22,0.25)] sm:p-10 lg:p-12'>
                        <ContactForm interests={interests} defaultInterest={defaultInterest} section={section} />
                    </div>
                </Reveal>
            </div>
        </section>
    )
}

export default ContactSection
